import { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import logger from 'Core-utils/logger';
import { replaceVideoJsIcons } from '../utils/videoJsIcons';

interface UseVideoPlayerOptions {
    videoUrl: string | null;
    hmacValidated: boolean;
    hash: string | undefined;
    onVideoEnded: () => void;
}

export const useVideoPlayer = ({ videoUrl, hmacValidated, hash, onVideoEnded }: UseVideoPlayerOptions) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<ReturnType<typeof videojs> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isInitializingRef = useRef(false);
    const isInitializedRef = useRef(false);

    useEffect(() => {
        logger.log('useEffect triggered', { hash, hasVideoRef: !!videoRef.current, hmacValidated, videoUrl });

        if (!hash || !hmacValidated || !videoUrl) {
            logger.log('Waiting for HMAC validation or video URL', { hash, hmacValidated, videoUrl });
            return;
        }

        const initPlayer = () => {
            // Prevent multiple simultaneous initializations
            if (isInitializingRef.current) {
                logger.log('initPlayer already in progress, skipping');
                return;
            }

            if (isInitializedRef.current && playerRef.current && !playerRef.current.isDisposed()) {
                logger.log('Player already initialized, skipping');
                return;
            }

            logger.log('initPlayer called');
            isInitializingRef.current = true;
            const videoElement = videoRef.current;
            if (!videoElement) {
                logger.error('Video element is null');
                return;
            }

            // More robust DOM check - ensure element is actually in the document
            if (!videoElement.parentNode || !document.body.contains(videoElement)) {
                logger.warn('Video element not in DOM yet, retrying...');
                setTimeout(initPlayer, 100);
                return;
            }

            // Check if Video.js is already initialized on this element
            try {
                const existingPlayer = videojs.getPlayer(videoElement);
                if (existingPlayer && !existingPlayer.isDisposed()) {
                    logger.log('Video.js player already exists, disposing...');
                    existingPlayer.dispose();
                }
            } catch (e) {
                logger.log('No existing player found (expected)', e);
            }

            // Dispose any existing player instance
            if (playerRef.current && !playerRef.current.isDisposed()) {
                logger.log('Disposing existing playerRef');
                playerRef.current.dispose();
                playerRef.current = null;
            }

            // Double-check element is still in DOM before initializing
            if (!videoElement.parentNode || !document.body.contains(videoElement)) {
                logger.error('Video element removed from DOM before initialization');
                return;
            }

            try {
                const player = videojs(videoElement, {
                    controls: true,
                    autoplay: false,
                    preload: 'metadata',
                    fluid: false, // Disable fluid mode - we'll handle sizing with CSS
                    responsive: false,
                    width: '100%',
                    height: '100%',
                    disablePictureInPicture: true,
                    experimentalSvgIcons: false,
                    controlBar: {
                        pictureInPictureToggle: false,
                    },
                    html5: {
                        vhs: {
                            overrideNative: true,
                        },
                        nativeVideoTracks: false,
                        nativeAudioTracks: false,
                        nativeTextTracks: false,
                    },
                    techOrder: ['html5'],
                    ...(videoUrl ? { src: videoUrl } : {}),
                });

                logger.log('Video.js player created', {
                    playerId: player.id(),
                    isDisposed: player.isDisposed(),
                });

                playerRef.current = player;
                isInitializedRef.current = true;

                // Helper function to attempt autoplay
                const attemptAutoplay = () => {
                    logger.log('Attempting to play video');
                    const playPromise = player.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                logger.log('Autoplay succeeded');
                            })
                            .catch((error: Error) => {
                                logger.warn('Autoplay prevented or failed:', error);
                                // Autoplay blocked - user will need to click play
                            });
                    }
                };

                // Wait for player to be ready
                player.ready(() => {
                    logger.log('Player ready callback fired');
                    isInitializingRef.current = false;
                    isInitializedRef.current = true;

                    // Ensure video source is set after player is ready
                    if (videoUrl) {
                        const currentSrc = player.currentSrc();
                        logger.log('Current source check', { currentSrc, videoUrl, match: currentSrc === videoUrl });
                        if (!currentSrc || currentSrc !== videoUrl) {
                            logger.log('Setting video source after player ready', videoUrl);
                            player.src({ type: 'video/mp4', src: videoUrl });
                            
                            // Wait for video to be ready to play before attempting autoplay
                            player.one('canplay', () => {
                                logger.log('Video can play, attempting autoplay');
                                attemptAutoplay();
                            });
                        } else {
                            // Source already set, wait for it to be ready
                            logger.log('Source already set, waiting for canplay event');
                            if (player.readyState() >= 3) {
                                // Video is already ready (HAVE_FUTURE_DATA or higher)
                                attemptAutoplay();
                            } else {
                                player.one('canplay', () => {
                                    logger.log('Video can play, attempting autoplay');
                                    attemptAutoplay();
                                });
                            }
                        }
                    }


                    // Replace Video.js icons with hollow react-icons
                    try {
                        replaceVideoJsIcons(player);
                        logger.log('Replaced Video.js icons with hollow react-icons');
                    } catch (error) {
                        logger.error('Failed to replace Video.js icons', error);
                    }

                    // Hide the no-js message once player is ready
                    const noJsMessage = videoElement.querySelector('.vjs-no-js');
                    if (noJsMessage) {
                        (noJsMessage as HTMLElement).style.display = 'none';
                        logger.log('Hidden no-js message');
                    }
                    setIsLoading(false);
                });

                // Handle loading states
                player.on('loadstart', () => {
                    logger.log('loadstart event');
                    setIsLoading(true);
                });

                player.on('canplay', () => {
                    logger.log('canplay event');
                    setIsLoading(false);
                });

                player.on('loadedmetadata', () => {
                    logger.log('loadedmetadata event', {
                        duration: player.duration(),
                        videoWidth: player.videoWidth(),
                        videoHeight: player.videoHeight(),
                        currentSrc: player.currentSrc(),
                        readyState: player.readyState(),
                    });
                });
                
                player.on('loadeddata', () => {
                    logger.log('loadeddata event', {
                        currentSrc: player.currentSrc(),
                        readyState: player.readyState(),
                        videoWidth: player.videoWidth(),
                        videoHeight: player.videoHeight(),
                    });
                });

                // Handle video errors
                player.on('error', () => {
                    setIsLoading(false);
                    const error = player.error();
                    const videoEl = player.el().querySelector('video');

                    logger.error('Video.js error:', error);
                    logger.error('Video element state:', {
                        networkState: videoEl?.networkState,
                        readyState: videoEl?.readyState,
                        src: videoEl?.src,
                        currentSrc: videoEl?.currentSrc,
                        error: videoEl?.error,
                    });

                    if (error) {
                        logger.error('Error details:', {
                            code: error.code,
                            message: error.message,
                        });
                    }
                });

                // Forward seek prevention logic
                let maxPlayedPosition = 0;
                let lastKnownTime = 0;

                const tech = player.tech({ IWillNotUseThisInPlugins: true });
                if (tech && (tech as any).vhs) {
                    (tech as any).vhs.bufferTimeAtTopQuality = 2;
                    (tech as any).vhs.bufferTimeDefault = 2;
                }

                player.on('timeupdate', () => {
                    const currentTime = player.currentTime() ?? 0;
                    if (currentTime > maxPlayedPosition) {
                        const timeDiff = currentTime - lastKnownTime;
                        if (timeDiff > 0 && timeDiff < 1) {
                            maxPlayedPosition = currentTime;
                        }
                    }
                    lastKnownTime = currentTime;
                });

                player.on('seeked', () => {
                    const seekTo = player.currentTime() ?? 0;
                    if (seekTo > maxPlayedPosition) {
                        logger.log('Prevented forward seek', {
                            attempted: seekTo.toFixed(2),
                            maxAllowed: maxPlayedPosition.toFixed(2),
                        });
                        player.currentTime(maxPlayedPosition);
                    }
                });

                player.on('seeking', () => {
                    const seekTo = player.currentTime() ?? 0;
                    if (seekTo > maxPlayedPosition) {
                        logger.log('Cancelling forward seek in progress', {
                            attempted: seekTo.toFixed(2),
                            maxAllowed: maxPlayedPosition.toFixed(2),
                        });
                        player.currentTime(maxPlayedPosition);
                    }
                });

                // Setup progress bar interceptor
                const setupProgressBarInterceptor = () => {
                    const progressHolder = player.el()?.querySelector('.vjs-progress-holder') as HTMLElement;
                    if (progressHolder) {
                        const updateMaxPositionIndicator = () => {
                            const duration = player.duration();
                            if (maxPlayedPosition > 0 && duration && duration > 0) {
                                const maxPercent = (maxPlayedPosition / duration) * 100;
                                let overlay = progressHolder.querySelector('.max-position-overlay') as HTMLElement;
                                if (!overlay) {
                                    overlay = document.createElement('div');
                                    overlay.className = 'max-position-overlay';
                                    overlay.style.cssText = `
                                        position: absolute;
                                        left: ${maxPercent}%;
                                        top: 0;
                                        right: 0;
                                        bottom: 0;
                                        background: rgba(0, 0, 0, 0.3);
                                        pointer-events: none;
                                        z-index: 5;
                                    `;
                                    progressHolder.appendChild(overlay);
                                } else {
                                    overlay.style.left = `${maxPercent}%`;
                                }
                            }
                        };

                        const handleProgressInteraction = (e: MouseEvent) => {
                            const rect = progressHolder.getBoundingClientRect();
                            const percent = (e.clientX - rect.left) / rect.width;
                            const seekTo = percent * (player.duration() || 0);

                            if (seekTo > maxPlayedPosition) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.stopImmediatePropagation();
                                logger.log('Prevented forward seek via progress bar', {
                                    attempted: seekTo.toFixed(2),
                                    maxAllowed: maxPlayedPosition.toFixed(2),
                                });
                                return false;
                            }
                        };

                        progressHolder.addEventListener('mousedown', handleProgressInteraction, true);
                        progressHolder.addEventListener('click', handleProgressInteraction, true);
                        progressHolder.addEventListener('mousemove', (e: MouseEvent) => {
                            if (player.duration()) {
                                const rect = progressHolder.getBoundingClientRect();
                                const percent = (e.clientX - rect.left) / rect.width;
                                const seekTo = percent * (player.duration() || 0);
                                if (seekTo > maxPlayedPosition) {
                                    e.stopPropagation();
                                    progressHolder.style.cursor = 'not-allowed';
                                } else {
                                    progressHolder.style.cursor = 'pointer';
                                }
                            }
                        }, true);

                        player.on('timeupdate', updateMaxPositionIndicator);
                    }

                    const handleKeyDown = (e: Event) => {
                        const keyEvent = e as KeyboardEvent;
                        if (keyEvent.key === 'ArrowLeft' || keyEvent.key === 'ArrowRight') {
                            const currentTime = player.currentTime() ?? 0;
                            const duration = player.duration() || 0;
                            const seekStep = keyEvent.key === 'ArrowRight' ? 10 : -10;
                            const newTime = Math.max(0, Math.min(duration, currentTime + seekStep));

                            if (newTime > maxPlayedPosition) {
                                e.preventDefault();
                                e.stopPropagation();
                                logger.log('Prevented forward seek via keyboard');
                            }
                        }
                    };
                    player.el()?.addEventListener('keydown', handleKeyDown, true);
                };

                player.ready(() => {
                    setTimeout(setupProgressBarInterceptor, 100);
                });

                player.on('progress', () => {
                    const buffered = player.buffered();
                    if (buffered.length > 0 && maxPlayedPosition > 0) {
                        const bufferedEnd = buffered.end(buffered.length - 1);
                        const maxAllowedBufferEnd = maxPlayedPosition + 2;
                        if (bufferedEnd > maxAllowedBufferEnd) {
                            logger.log('Buffer exceeded max allowed', {
                                bufferedEnd: bufferedEnd.toFixed(2),
                                maxAllowed: maxAllowedBufferEnd.toFixed(2),
                                maxPlayed: maxPlayedPosition.toFixed(2),
                            });
                        }
                    }
                });

                player.on('ended', () => {
                    onVideoEnded();
                });
            } catch (error) {
                isInitializingRef.current = false;
                logger.error('Error initializing Video.js:', error);
                logger.error('Error stack:', error instanceof Error ? error.stack : 'No stack');
                return;
            }
        };

        // Use a timeout as a fallback if RAF doesn't work
        logger.log('Setting up initialization timers');
        const timeoutId = setTimeout(() => {
            logger.log('Timeout callback fired');
            if (isInitializedRef.current || isInitializingRef.current) {
                logger.log('Already initialized or initializing, skipping timeout init');
                return;
            }
            const videoElement = videoRef.current;
            if (videoElement && videoElement.parentNode && document.body.contains(videoElement)) {
                logger.log('Element ready in timeout, scheduling initPlayer');
                requestAnimationFrame(() => {
                    initPlayer();
                });
            }
        }, 100);

        // Also try with requestAnimationFrame as a fallback
        let frameCount = 0;
        const checkAndInit = () => {
            frameCount++;
            logger.log('RAF check', { frameCount });
            const videoElement = videoRef.current;
            if (videoElement && videoElement.parentNode && document.body.contains(videoElement)) {
                logger.log('Element ready in RAF, checking if init needed');
                if (!isInitializedRef.current && !isInitializingRef.current && (!playerRef.current || playerRef.current.isDisposed())) {
                    logger.log('Calling initPlayer from RAF');
                    initPlayer();
                }
            } else {
                if (frameCount < 5) {
                    requestAnimationFrame(checkAndInit);
                } else {
                    logger.warn('Max RAF retries reached');
                }
            }
        };
        const rafId = requestAnimationFrame(checkAndInit);

        return () => {
            logger.log('Cleanup: disposing player and clearing timers', {
                isInitializing: isInitializingRef.current,
                isInitialized: isInitializedRef.current,
                hasPlayer: !!playerRef.current,
                playerDisposed: playerRef.current?.isDisposed(),
            });
            clearTimeout(timeoutId);
            cancelAnimationFrame(rafId);
            
            // Only dispose if we're not currently initializing
            // This prevents React StrictMode from disposing a player that's still being set up
            if (playerRef.current && !isInitializingRef.current) {
                logger.log('Disposing player in cleanup', {
                    playerId: playerRef.current.id(),
                    isDisposed: playerRef.current.isDisposed(),
                });
                try {
                    if (!playerRef.current.isDisposed()) {
                        playerRef.current.dispose();
                    }
                } catch (error) {
                    logger.warn('Error disposing player:', error);
                }
                playerRef.current = null;
            } else if (isInitializingRef.current) {
                logger.log('Skipping disposal - player is still initializing');
            }
            
            isInitializingRef.current = false;
            isInitializedRef.current = false;
        };
    }, [hash, hmacValidated, videoUrl, onVideoEnded]);

    return { videoRef, isLoading };
};

